import { IconPhoto, IconUpload } from "@tabler/icons-react"
import { type FC, type ReactNode, useId, useState } from "react"
import { useDropzone } from "react-dropzone"

import { cn } from "@forento/ui/utilities/tailwind"

import { actionClass } from "~/components/Button"

export const fieldClass =
	"w-full min-w-0 rounded-lg border border-component-border bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/10 disabled:bg-component-background disabled:text-gray-400"

export const SettingsPanel: FC<{
	title: string
	description: string
	icon: ReactNode
	children: ReactNode
}> = ({ title, description, icon, children }) => {
	const headingId = useId()
	return (
		<section aria-labelledby={headingId}>
			<header className="flex items-center gap-3 p-4 sm:px-5">
				<span className="bg-component-background text-primary flex size-9 shrink-0 items-center justify-center rounded-xl">
					{icon}
				</span>
				<div className="min-w-0 flex-1">
					<h2 id={headingId} className="text-sm font-semibold">
						{title}
					</h2>
					<p className="mt-0.5 text-xs leading-relaxed text-gray-500">{description}</p>
				</div>
			</header>
			<div className="border-component-border bg-component-background/40 border-t px-4 py-4 sm:px-5">
				{children}
			</div>
		</section>
	)
}

export const SettingsSection: FC<{ title?: string; description?: string; children: ReactNode }> = ({
	title,
	description,
	children,
}) => {
	const id = useId()
	return (
		<section aria-labelledby={title ? id : undefined} className="mb-5 last:mb-0">
			{title && (
				<header className="border-component-border border-b pb-3">
					<h3 id={id} className="text-xs font-semibold tracking-wider text-gray-500 uppercase">
						{title}
					</h3>
					{description && <p className="mt-1 text-xs leading-relaxed text-gray-500">{description}</p>}
				</header>
			)}
			<div className="divide-component-border divide-y">{children}</div>
		</section>
	)
}

export const SettingsRow: FC<{ label: string; description?: ReactNode; htmlFor?: string; children: ReactNode }> = ({
	label,
	description,
	htmlFor,
	children,
}) => (
	<div className="grid gap-3 py-3 sm:grid-cols-[minmax(0,0.8fr)_minmax(0,1.4fr)] sm:gap-6">
		<div className="min-w-0 pt-1">
			{htmlFor ? (
				<label htmlFor={htmlFor} className="text-sm font-medium text-gray-800">
					{label}
				</label>
			) : (
				<h3 className="text-sm font-medium text-gray-800">{label}</h3>
			)}
			{description && <p className="mt-1 max-w-xs text-xs leading-relaxed text-gray-500">{description}</p>}
		</div>
		<div className="min-w-0">{children}</div>
	</div>
)

export const SettingSwitch: FC<{
	label: string
	checked: boolean
	onChange(value: boolean): void
	disabled?: boolean
}> = ({ label, checked, onChange, disabled }) => (
	<button
		type="button"
		role="switch"
		aria-label={label}
		aria-checked={checked}
		disabled={disabled}
		onClick={() => onChange(!checked)}
		className="group focus-visible:outline-primary inline-flex items-center gap-2.5 rounded py-1 focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-wait disabled:opacity-50"
	>
		<span
			aria-hidden="true"
			className={cn(
				"flex h-5 w-9 items-center rounded-full p-0.5 transition-colors",
				checked ? "bg-primary" : "bg-stone-300",
			)}
		>
			<span
				className={cn(
					"size-4 rounded-full bg-white shadow-sm transition-transform",
					checked && "translate-x-4",
				)}
			/>
		</span>
		<span className="text-sm text-gray-600">{checked ? "Enabled" : "Disabled"}</span>
	</button>
)

export const AcademyImageUpload: FC<{
	label: string
	hint: string
	filePath: string | null
	onUpload(file: File): Promise<unknown>
	onDelete(): Promise<unknown>
}> = ({ label, hint, filePath, onUpload, onDelete }) => {
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const run = async (action: () => Promise<unknown>) => {
		setBusy(true)
		setError(null)
		try {
			await action()
		} catch {
			setError("Your image could not be saved. Please try again.")
		} finally {
			setBusy(false)
		}
	}
	const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
		multiple: false,
		disabled: busy,
		noClick: true,
		noKeyboard: true,
		accept: {
			"image/png": [".png"],
			"image/jpeg": [".jpg", ".jpeg"],
			"image/webp": [".webp"],
			"image/gif": [".gif"],
			"image/svg+xml": [".svg"],
		},
		onDrop: (files, rejected) => {
			if (rejected.length) {
				setError("Choose a PNG, JPEG, WebP, GIF, or SVG image.")
				return
			}
			const file = files[0]
			if (file) void run(() => onUpload(file))
		},
	})
	return (
		<div>
			<div
				{...getRootProps()}
				className={cn(
					"border-component-border flex items-center gap-3 rounded-lg border border-dashed p-3",
					isDragActive && "border-primary bg-primary/5",
				)}
			>
				<input {...getInputProps()} aria-label={`Upload ${label.toLowerCase()}`} />
				<div className="border-component-border bg-component-background flex size-14 shrink-0 items-center justify-center overflow-hidden rounded border p-1.5">
					{filePath ? (
						<img src={filePath} alt={label} className="max-h-full max-w-full object-contain" />
					) : (
						<IconPhoto size={22} stroke={1.4} className="text-gray-400" aria-hidden="true" />
					)}
				</div>
				<div className="min-w-0">
					<div className="flex flex-wrap items-center gap-2">
						<button
							type="button"
							aria-label={`${filePath ? "Replace" : "Upload"} ${label.toLowerCase()}`}
							disabled={busy}
							onClick={open}
							className={actionClass}
						>
							<IconUpload size={14} aria-hidden="true" />
							{busy ? "Saving…" : filePath ? "Replace" : "Upload"}
						</button>
						{filePath && (
							<button
								type="button"
								disabled={busy}
								aria-label={`Remove ${label.toLowerCase()}`}
								onClick={() => void run(onDelete)}
								className="hover:text-danger rounded px-1 py-1.5 text-xs text-gray-500 focus-visible:outline-2 disabled:opacity-50"
							>
								Remove
							</button>
						)}
					</div>
					<p className="mt-1.5 text-xs text-gray-500">{isDragActive ? "Drop image here" : hint}</p>
				</div>
			</div>
			{error && (
				<p role="alert" className="text-danger mt-2 text-xs">
					{error}
				</p>
			)}
		</div>
	)
}
